import React, { useState, useEffect, useCallback } from "react";
import {
  FiUpload,
  FiFileText,
  FiAlertCircle,
  FiArrowRight,
  FiCheck,
  FiDownload,
  FiSave,
  FiRefreshCw,
} from 'react-icons/fi';
import { getTrans as t } from "../../Localization/translations";
import JsonServices from "../../services/Json/JsonServices";

// Define fields for each table with 'required' based on Import.js CSVConfig
const ATHENA_PARTY_FIELDS = [
  { key: "party_name", label: "Party Name", required: true },
  { key: "party_type", label: "Party Type", required: true },
  { key: "entity_type", label: "Entity Type", required: true },
  { key: "tax_number", label: "Tax Number", required: false },
  { key: "establishment_date", label: "Establishment Date", required: false },
  { key: "sales_tax_code", label: "Sales Tax Code", required: false },
  { key: "website", label: "Website", required: false },
  { key: "prefix", label: "Prefix", required: false },
  { key: "first_name", label: "First Name", required: false },
  { key: "middle_name", label: "Middle Name", required: false },
  { key: "last_name", label: "Last Name", required: false },
  { key: "ssn_tin_number", label: "SSN / TIN Number", required: false },
  { key: "id_number", label: "Id Number", required: false },
  { key: "id_issue_state", label: "Id Issue State", required: false },
  { key: "date_of_birth", label: "Date of Birth", required: false },
  { key: "doing_business_as", label: "Doing Business As", required: false },
  { key: "business_type", label: "Business Type", required: false },
  { key: "federal_id", label: "Federal ID", required: false },
  { key: "sic", label: "SIC", required: false },
  { key: "naics", label: "NAICS", required: false },
  { key: "risk_rating", label: "Risk Rating", required: false },
  { key: "credit_score", label: "Credit Score", required: false }
];

const ATHENA_CONTRACT_FIELDS = [
  { key: "contract_type", label: "Contract Type", required: true },
  { key: "customer_party_reference", label: "Customer(Party Reference)", required: true },
  { key: "bill_to_address_reference", label: "Bill to Address(Address Reference)", required: true },
  { key: "irr_interest_pa", label: "IRR/Interest p.a. (%)", required: false },
  { key: "term", label: "Term", required: true },
  { key: "start_date", label: "Start Date", required: true },
  { key: "payment_rule", label: "Payment Rule", required: true },
  { key: "payment_frequency", label: "Payment Frequency", required: true },
  { key: "periodic_payment_amount", label: "Periodic Payment Amount", required: false },
  { key: "app_originator", label: "App Originator", required: false },
  { key: "source", label: "Source", required: false },
  { key: "sales_rep", label: "Sales Rep", required: false },
];

const ATHENA_CONTRACTPAYMENT_FIELDS = [
  { key: "contract_number", label: "Contract Number", required: true },
  { key: "no_of_payments", label: "No of Payments", required: true },
  { key: "payment_amount", label: "Payment Amount", required: true },
  { key: "payment_type", label: "Payment Type", required: true },
  { key: "start_date", label: "Start Date", required: false },
  { key: "end_date", label: "End Date", required: false },
];

const ATHENA_CONTRACTASSETS_FIELDS = [
  { key: "contract_number", label: "Contract Number", required: true },
  { key: "asset_desc", label: "Asset Desc", required: false },
  { key: "license_no", label: "License No", required: false },
  { key: "year", label: "Year", required: false },
  { key: "vin_number", label: "VIN Number", required: false },
  { key: "license_state", label: "License State", required: false },
  { key: "manufacturer", label: "Manufacturer", required: false },
  { key: "make", label: "Make", required: false },
  { key: "model", label: "Model", required: false },
  { key: "user_purpose", label: "User Purpose", required: false },
  { key: "asset_type", label: "Asset Type", required: false },
  { key: "residual_value", label: "Residual Value", required: true },
  { key: "installed_location_address_reference", label: "Installed Location(Address Reference)", required: true },
  { key: "vendor_name_party_reference", label: "Vendor Name(Party Reference)", required: true },
  { key: "delivery_date", label: "Delivery Date", required: false },
  { key: "in_service_date", label: "In-Service Date", required: false },
  { key: "milage", label: "Milage", required: false },
  { key: "vehicle_cost", label: "Vehicle Cost", required: true },
  { key: "vehicle_cost_pay_to", label: "Vehicle Cost Pay To", required: false },
  { key: "tax", label: "Tax", required: false },
  { key: "tax_pay_to", label: "Tax Pay To", required: false },
  { key: "trade_in", label: "Trade In", required: false },
  { key: "trade_in_pay_to", label: "Trade In Pay To", required: false },
  { key: "registration", label: "Registration", required: false },
  { key: "registration_pay_to", label: "Registration Pay To", required: false },
  { key: "down_payment", label: "Down Payment", required: false },
  { key: "down_payment_pay_to", label: "Down Payment Pay To", required: false },
  { key: "condition", label: "Condition", required: false },
  { key: "vendor_asset_id", label: "Vendor Asset ID", required: false },
  { key: "salvage_value", label: "Salvage Value", required: false },
];

const ATHENA_PARTYCONTACT_FIELDS = [
  { key: "party_reference", label: "Party Reference", required: true },
  { key: "prefix", label: "Prefix", required: false },
  { key: "first_name", label: "First Name", required: true },
  { key: "middle_name", label: "Middle Name", required: false },
  { key: "last_name", label: "Last Name", required: false },
  { key: "phone_no", label: "Phone No", required: false },
  { key: "mobile_no", label: "Mobile No", required: false },
  { key: "email_address", label: "Email Address", required: false },
];

const ATHENA_PARTYADDRESS_FIELDS = [
  { key: "party_reference", label: "Party Reference", required: true },
  { key: "address_type", label: "Address Line 1", required: true },
  { key: "street", label: "Address Line 2", required: true },
  { key: "country", label: "Country", required: true },
  { key: "state", label: "State", required: true },
  { key: "city", label: "City", required: true },
  { key: "zip", label: "Zip Code", required: true },
  { key: "bill_to", label: "Bill To", required: false },
  { key: "ship_to", label: "Ship To", required: false },
  { key: "install_to", label: "Install To", required: false },
];

const TABLE_FIELDS = {
  party: ATHENA_PARTY_FIELDS,
  partyaddress: ATHENA_PARTYADDRESS_FIELDS,
  partycontact: ATHENA_PARTYCONTACT_FIELDS,
  contract: ATHENA_CONTRACT_FIELDS,
  contractpayment: ATHENA_CONTRACTPAYMENT_FIELDS,
  contractassets: ATHENA_CONTRACTASSETS_FIELDS,
};

const DATABASE_TABLES = [
  { name: "", label: "Select" },
  { name: "party", label: "Party" },
  { name: "partyaddress", label: "Party Address" },
  { name: "partycontact", label: "Party Contact" },
  { name: "contract", label: "Contract" },
  { name: "contractpayment", label: "Payment Schedule" },
  { name: "contractassets", label: "Assets" },
];

const importSequence = ['party', 'partycontact', 'partyaddress', 'contract', 'contractpayment', 'contractassets'];

// Extract all JSON field paths
function analyzeJsonStructure(obj, path = "", fields = []) {
  if (obj === null || obj === undefined) return fields;
  if (Array.isArray(obj)) {
    if (obj.length > 0) {
      analyzeJsonStructure(obj[0], path + "[0]", fields);
    }
    return fields;
  }
  if (typeof obj === "object") {
    Object.keys(obj).forEach((key) => {
      const currentPath = path ? `${path}.${key}` : key;
      const value = obj[key];
      if (Array.isArray(value)) {
        if (value.length > 0 && typeof value[0] === "object") {
          analyzeJsonStructure(value[0], `${currentPath}[0]`, fields);
        } else {
          fields.push({ path: currentPath, type: "array", sample: value.slice(0, 3) });
        }
      } else if (typeof value === "object" && value !== null) {
        analyzeJsonStructure(value, currentPath, fields);
      } else {
        fields.push({ path: currentPath, type: typeof value, sample: value });
      }
    });
  }
  return fields;
}

// Enhanced auto-map function with context-aware matching
function autoMapField(athenaKey, jsonFields, tableName) {
  const lowerKey = athenaKey.toLowerCase().replace(/_/g, '').replace(/ /g, '');
  const keyWords = lowerKey.split(/(?=[A-Z])/).map(word => word.toLowerCase());

  // Context-specific synonyms to avoid cross-table mismatches
  const synonyms = {
    // Party Contact fields
    party_reference: ['partyreference', 'partyid', 'customerid', 'clientid'],
    prefix: ['prefix', 'title', 'salutation', 'honorific'],
    first_name: ['firstname', 'givenname', 'fname', 'forename', 'name'],
    middle_name: ['middlename', 'mname'],
    last_name: ['lastname', 'surname', 'lname', 'familyname'],
    phone_no: ['phoneno', 'phone', 'phonenumber', 'contactnumber'],
    mobile_no: ['mobileno', 'mobile', 'cellphone', 'cell'],
    email_address: ['emailaddress', 'email', 'mail', 'emailid'],
    // Party Address fields
    address_type: ['addresstype', 'type', 'addrtype', 'addressline1', 'line1'],
    street: ['street', 'addressline2', 'addr2', 'streetaddress'],
    country: ['country', 'nation'],
    state: ['state', 'region', 'province'],
    city: ['city', 'town'],
    zip: ['zip', 'postalcode', 'zipcode', 'postcode'],
    bill_to: ['billto', 'billing'],
    ship_to: ['shipto', 'shipping'],
    install_to: ['installto', 'installation'],
    // Party fields
    party_name: ['partyname', 'name', 'customername', 'fullname', 'clientname', 'businessname'],
    party_type: ['partytype', 'type', 'category', 'kind'],
    entity_type: ['entitytype', 'entity', 'customertype', 'clienttype'],
    tax_number: ['taxnumber', 'tax', 'vat', 'taxid'],
    establishment_date: ['establishmentdate', 'startdate', 'createdate', 'formationdate'],
    sales_tax_code: ['salestaxcode', 'taxcode', 'salestax'],
    website: ['website', 'url', 'site', 'web'],
    ssn_tin_number: ['ssntinnumber', 'ssn', 'tin', 'socialsecurity', 'taxidnumber'],
    id_number: ['idnumber', 'id', 'identification', 'identnumber'],
    id_issue_state: ['idissuestate', 'issuestate', 'idstate'],
    date_of_birth: ['dateofbirth', 'dob', 'birthdate', 'born'],
    doing_business_as: ['doingbusinessas', 'dba', 'businessname', 'tradingas'],
    business_type: ['businesstype', 'type', 'industry', 'buscat'],
    federal_id: ['federalid', 'fedid', 'ein', 'federaltaxid'],
    sic: ['sic', 'standardindustrialclassification', 'siccode'],
    naics: ['naics', 'northamericanindustryclassification', 'naicscode'],
    risk_rating: ['riskrating', 'risk', 'rating', 'risklevel'],
    credit_score: ['creditscore', 'credit', 'score', 'creditrating'],
    // Contract fields
    contract_type: ['contracttype', 'type', 'contractcategory'],
    customer_party_reference: ['customer', 'partyreference', 'customerid', 'partyid', 'clientid'],
    bill_to_address_reference: ['billto', 'addressreference', 'billingaddress', 'billaddress'],
    irr_interest_pa: ['irr', 'interest', 'rate', 'interestrate'],
    term: ['term', 'duration', 'period'],
    start_date: ['startdate', 'begindate', 'commencedate'],
    payment_rule: ['paymentrule', 'rule', 'paymentterms'],
    payment_frequency: ['paymentfrequency', 'frequency', 'payfreq'],
    periodic_payment_amount: ['periodicpayment', 'paymentamount', 'installment', 'payamt'],
    app_originator: ['apporiginator', 'originator', 'sourceapp'],
    source: ['source', 'origin'],
    sales_rep: ['salesrep', 'representative', 'salesperson', 'agent'],
    // Contract Payment fields
    contract_number: ['contractnumber', 'number', 'id', 'contractid'],
    no_of_payments: ['noofpayments', 'paymentscount', 'numofpayments'],
    payment_amount: ['paymentamount', 'amount', 'payamt'],
    payment_type: ['paymenttype', 'type', 'paytype'],
    end_date: ['enddate', 'duedate', 'expirydate'],
    // Contract Assets fields
    asset_desc: ['assetdesc', 'description', 'assetdescription'],
    license_no: ['licenseno', 'license', 'licnumber'],
    year: ['year', 'yr'],
    vin_number: ['vin', 'vinnumber', 'vehicleid'],
    license_state: ['licensestate', 'state', 'licstate'],
    manufacturer: ['manufacturer', 'maker', 'mfg'],
    make: ['make', 'brand'],
    model: ['model'],
    user_purpose: ['userpurpose', 'purpose', 'use'],
    asset_type: ['assettype', 'type', 'assetcategory'],
    residual_value: ['residualvalue', 'residual', 'resvalue'],
    installed_location_address_reference: ['installedlocation', 'addressreference', 'installaddr'],
    vendor_name_party_reference: ['vendor', 'partyreference', 'vendorid'],
    delivery_date: ['deliverydate', 'delivered'],
    in_service_date: ['inservicedate', 'servicedate'],
    milage: ['milage', 'mileage', 'miles'],
    vehicle_cost: ['vehiclecost', 'cost', 'price'],
    vehicle_cost_pay_to: ['vehiclecostpayto', 'payto', 'vehiclepay'],
    tax: ['tax', 'salestax'],
    tax_pay_to: ['taxpayto', 'taxpay'],
    trade_in: ['tradein', 'trade'],
    trade_in_pay_to: ['tradeinpayto', 'tradeinpay'],
    registration: ['registration', 'reg'],
    registration_pay_to: ['registrationpayto', 'regpay'],
    down_payment: ['downpayment', 'downpay'],
    down_payment_pay_to: ['downpaymentpayto', 'downpayto'],
    condition: ['condition', 'status'],
  };

  // Expected data types for fields
  const expectedTypes = {
    party_reference: ['string', 'number'],
    prefix: ['string'],
    first_name: ['string'],
    middle_name: ['string'],
    last_name: ['string'],
    phone_no: ['string', 'number'],
    mobile_no: ['string', 'number'],
    email_address: ['string'],
    address_type: ['string'],
    street: ['string'],
    country: ['string'],
    state: ['string'],
    city: ['string'],
    zip: ['string', 'number'],
    bill_to: ['string', 'boolean'],
    ship_to: ['string', 'boolean'],
    install_to: ['string', 'boolean'],
    party_name: ['string'],
    party_type: ['string'],
    entity_type: ['string'],
    tax_number: ['string', 'number'],
    establishment_date: ['string'],
    sales_tax_code: ['string'],
    website: ['string'],
    ssn_tin_number: ['string', 'number'],
    id_number: ['string', 'number'],
    id_issue_state: ['string'],
    date_of_birth: ['string'],
    doing_business_as: ['string'],
    business_type: ['string'],
    federal_id: ['string', 'number'],
    sic: ['string', 'number'],
    naics: ['string', 'number'],
    risk_rating: ['string', 'number'],
    credit_score: ['number'],
    contract_type: ['string'],
    customer_party_reference: ['string', 'number'],
    bill_to_address_reference: ['string', 'number'],
    irr_interest_pa: ['number'],
    term: ['number', 'string'],
    start_date: ['string'],
    payment_rule: ['string'],
    payment_frequency: ['string'],
    periodic_payment_amount: ['number'],
    app_originator: ['string'],
    source: ['string'],
    sales_rep: ['string'],
    contract_number: ['string', 'number'],
    no_of_payments: ['number'],
    payment_amount: ['number'],
    payment_type: ['string'],
    end_date: ['string'],
    asset_desc: ['string'],
    license_no: ['string', 'number'],
    year: ['number', 'string'],
    vin_number: ['string', 'number'],
    license_state: ['string'],
    manufacturer: ['string'],
    make: ['string'],
    model: ['string'],
    user_purpose: ['string'],
    asset_type: ['string'],
    residual_value: ['number'],
    installed_location_address_reference: ['string', 'number'],
    vendor_name_party_reference: ['string', 'number'],
    delivery_date: ['string'],
    in_service_date: ['string'],
    milage: ['number'],
    vehicle_cost: ['number'],
    vehicle_cost_pay_to: ['string'],
    tax: ['number'],
    tax_pay_to: ['string'],
    trade_in: ['number'],
    trade_in_pay_to: ['string'],
    registration: ['number'],
    registration_pay_to: ['string'],
    down_payment: ['number'],
    down_payment_pay_to: ['string'],
    condition: ['string'],
  };

  // Table-specific keywords to boost or penalize matches
  const tableContext = {
    partycontact: ['contact', 'email', 'phone', 'name'],
    partyaddress: ['address', 'street', 'city', 'state', 'zip', 'country'],
    party: ['party', 'name', 'type', 'tax', 'entity'],
    contract: ['contract', 'type', 'term', 'payment'],
    contractpayment: ['payment', 'amount', 'frequency'],
    contractassets: ['asset', 'vehicle', 'vin', 'manufacturer'],
  };

  let bestMatch = '';
  let highestScore = 0;

  // Simple Levenshtein distance for fuzzy matching
  const levenshteinDistance = (a, b) => {
    const matrix = Array(b.length + 1).fill(null).map(() => Array(a.length + 1).fill(0));
    for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= b.length; j++) matrix[j][0] = j;
    for (let j = 1; j <= b.length; j++) {
      for (let i = 1; i <= a.length; i++) {
        const indicator = a[i - 1] === b[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j - 1][i] + 1,
          matrix[j][i - 1] + 1,
          matrix[j - 1][i - 1] + indicator
        );
      }
    }
    return matrix[b.length][a.length];
  };

  for (const field of jsonFields) {
    const lowerPath = field.path.toLowerCase().replace(/_/g, '').replace(/\./g, '');
    let score = 0;

    // Exact or near-exact match
    if (lowerPath === lowerKey || lowerPath.includes(lowerKey)) {
      score += 100;
    }

    // Synonym match
    if (synonyms[athenaKey]) {
      synonyms[athenaKey].forEach(synonym => {
        if (lowerPath.includes(synonym)) {
          score += 80;
        }
      });
    }

    // Keyword match
    keyWords.forEach(word => {
      if (lowerPath.includes(word)) {
        score += 30;
      }
    });

    // Fuzzy match
    const distance = levenshteinDistance(lowerKey, lowerPath);
    score += Math.max(0, 50 - distance * 5);

    // Type compatibility
    if (expectedTypes[athenaKey] && expectedTypes[athenaKey].includes(field.type)) {
      score += 20;
    }

    // Context-based scoring
    if (tableContext[tableName]) {
      tableContext[tableName].forEach(contextWord => {
        if (lowerPath.includes(contextWord)) {
          score += 20;
        }
      });
      // Penalize irrelevant contexts
      Object.keys(tableContext).forEach(otherTable => {
        if (otherTable !== tableName) {
          tableContext[otherTable].forEach(contextWord => {
            if (lowerPath.includes(contextWord)) {
              score -= 30;
            }
          });
        }
      });
    }

    // Penalize nested paths lightly
    score -= field.path.split('.').length * 2;

    if (score > highestScore && score >= 20) {
      highestScore = score;
      bestMatch = field.path;
    }
  }

  console.log(`Auto-mapping ${tableName}.${athenaKey}: ${bestMatch || 'No match'} (Score: ${highestScore})`);
  return bestMatch;
}

// Validate mappings for correct fields
function validateMappings(tableName, mappings, athenaFields) {
  const validFields = athenaFields.map(f => f.key);
  const invalidFields = Object.keys(mappings).filter(key => !validFields.includes(key));
  return invalidFields.length === 0 ? null : `Invalid fields for ${tableName}: ${invalidFields.join(', ')}`;
}

// Function to generate CSV content
const generateCsv = (data, mappings, tableName, overrides = {}) => {
  const headers = mappings.map(m => m.label);
  const rows = [];

  const dataArray = Array.isArray(data) ? data : [data];
  dataArray.forEach((record, index) => {
    const row = {};
    mappings.forEach(m => {
      let value = m.defaultValue || "";
      if (m.jsonPath && !overrides[m.athenaField]) {
        try {
          value = m.jsonPath.split(".").reduce((o, segment, idx, path) => {
            if (o == null) return "";
            const arrayMatch = segment.match(/(.*?)\[(\d+)\]/);
            if (arrayMatch) {
              const [, key, indexStr] = arrayMatch;
              const index = parseInt(indexStr);
              const arr = key ? o[key] : o;
              return arr?.[index] ?? "";
            } else {
              const result = o[segment] ?? "";
              if (
                typeof result === 'object' &&
                result !== null &&
                !Array.isArray(result) &&
                idx === path.length - 1
              ) {
                return segment;
              }
              return result;
            }
          }, record);
        } catch (e) {
          console.warn(`Error accessing JSON path ${m.jsonPath} for record ${index}:`, e);
          value = "";
        }
      }
      if (overrides[m.athenaField]) {
        value = overrides[m.athenaField](index);
      }
      row[m.label] = String(value).trim();
    });
    rows.push(row);
  });

  const escapeCsvValue = (value) => {
    if (value == null) return '';
    const stringValue = String(value).trim();
    return `"${stringValue.replace(/"/g, '""')}"`;
  };

  const csvRows = [
    headers.map(escapeCsvValue).join(','),
    ...rows.map(row => headers.map(header => escapeCsvValue(row[header])).join(','))
  ];

  return csvRows.join('\n');
};

export default function JsonToDbImport() {
  const totalSteps = 4;
  const [currentStep, setCurrentStep] = useState(1);
  const [jsonData, setJsonData] = useState(null);
  const [jsonFields, setJsonFields] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [tableMappings, setTableMappings] = useState([]);
  const [jsonPreview, setJsonPreview] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [mappingsList, setMappingsList] = useState([]);
  const [selectedMappingId, setSelectedMappingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importErrors, setImportErrors] = useState({});
  const [importStatus, setImportStatus] = useState(
    importSequence.reduce((acc, table) => ({ ...acc, [table]: 'pending' }), {})
  );
  const [isImporting, setIsImporting] = useState(false);
  const [refs, setRefs] = useState({
    partyRefs: [],
    addressRefs: [],
    contractRefs: [],
  });
  const [unmappedFields, setUnmappedFields] = useState([]);
  const [invalidFieldsError, setInvalidFieldsError] = useState(null);

  // Define table dependencies for reference injection
  const dependencies = {
    partycontact: { party_reference: 'partyRefs' },
    partyaddress: { party_reference: 'partyRefs' },
    contract: {
      customer_party_reference: 'partyRefs',
      bill_to_address_reference: 'addressRefs',
      vendor_name_party_reference: 'partyRefs',
    },
    contractpayment: { contract_number: 'contractRefs' },
    contractassets: {
      contract_number: 'contractRefs',
      installed_location_address_reference: 'addressRefs',
      vendor_name_party_reference: 'partyRefs',
    },
  };

  // Define how to extract references from API responses
  const refExtractors = {
    party: {
      arrayKey: 'successful_parties',
      refKey: 'party_ref',
      refName: 'partyRefs',
    },
    partyaddress: {
      arrayKey: 'successful_addresses',
      refKey: 'address_ref',
      refName: 'addressRefs',
    },
    contract: {
      arrayKey: 'successful_contracts',
      refKey: 'contract_number',
      refName: 'contractRefs',
    },
  };

  // Fetch and initialize mappings
  useEffect(() => {
    const fetchMappings = async () => {
      setIsLoading(true);
      try {
        const response = await JsonServices.listMappings();
        if (response.success) {
          setMappingsList(response.mappings);
          // Create mappings for any missing tables
          for (const table of importSequence) {
            if (!response.mappings.find(m => m.table_name === table)) {
              const athenaFields = TABLE_FIELDS[table] || [];
              const autoMappings = {};
              const unmapped = [];
              athenaFields.forEach((field) => {
                if (dependencies[table] && field.key in dependencies[table]) {
                  autoMappings[field.key] = '';
                } else {
                  const mappedField = autoMapField(field.key, jsonFields, table);
                  autoMappings[field.key] = mappedField;
                  if (!mappedField && field.required) {
                    unmapped.push(field.label);
                  }
                }
              });
              await JsonServices.saveMapping({ table_name: table, mappings: autoMappings });
              setUnmappedFields(unmapped);
            }
          }
          // Refresh mappings list
          const updatedResponse = await JsonServices.listMappings();
          if (updatedResponse.success) {
            setMappingsList(updatedResponse.mappings);
          }
        } else {
          setErrorMessage("Failed to fetch mappings.");
        }
      } catch (error) {
        setErrorMessage("Error initializing mappings.");
      } finally {
        setIsLoading(false);
      }
    };
    if (jsonFields.length > 0) {
      fetchMappings();
    }
  }, [jsonFields]);

  // Handle JSON file upload
  const handleJsonUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) {
      setErrorMessage("No file selected.");
      return;
    }
    if (!file.name.endsWith('.json')) {
      setErrorMessage("File must be a JSON file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsedJson = JSON.parse(e.target.result);
        if (!parsedJson || (Array.isArray(parsedJson) && parsedJson.length === 0) || (typeof parsedJson === 'object' && Object.keys(parsedJson).length === 0)) {
          throw new Error("JSON is empty or has no data.");
        }
        setJsonData(parsedJson);
        setJsonPreview(JSON.stringify(parsedJson, null, 2));
        setErrorMessage(null);
        setCurrentStep(2);
      } catch (err) {
        setErrorMessage("Invalid JSON file. Please upload a valid JSON with data.");
      }
    };
    reader.readAsText(file);
  }, []);

  // Analyze JSON structure
  useEffect(() => {
    if (jsonData) {
      const fields = analyzeJsonStructure(jsonData);
      if (fields.length === 0) {
        setErrorMessage("No fields found in JSON structure.");
      } else {
        setJsonFields(fields);
      }
    }
  }, [jsonData]);

  // Load mappings for selected table
  const startMapping = useCallback(async (tableName, externalMappingsList = null) => {
    if (!tableName) {
      setSelectedTable("");
      setTableMappings([]);
      setSelectedMappingId(null);
      setUnmappedFields([]);
      setInvalidFieldsError(null);
      setCurrentStep(3);
      return;
    }

    setIsLoading(true);
    setSelectedTable(tableName);
    setErrorMessage(null);
    setSuccessMessage(null);
    setTableMappings([]);
    setUnmappedFields([]);
    setInvalidFieldsError(null);

    const mappingsToUse = externalMappingsList || mappingsList;
    const athenaFields = TABLE_FIELDS[tableName] || [];
    const tableLabel = DATABASE_TABLES.find(t => t.name === tableName)?.label || tableName;

    try {
      let existingMapping = mappingsToUse.find(m => m.table_name === tableName);
      let usedMappings = {};
      const unmapped = [];

      if (!existingMapping) {
        const autoMappings = {};
        athenaFields.forEach((field) => {
          if (dependencies[tableName] && field.key in dependencies[tableName]) {
            autoMappings[field.key] = '';
          } else {
            const mappedField = autoMapField(field.key, jsonFields, tableName);
            autoMappings[field.key] = mappedField;
            if (!mappedField && field.required) {
              unmapped.push(field.label);
            }
          }
        });
        const validationError = validateMappings(tableName, autoMappings, athenaFields);
        if (validationError) {
          console.error(validationError);
          setInvalidFieldsError(validationError);
        }
        const response = await JsonServices.saveMapping({ table_name: tableName, mappings: autoMappings });
        setSelectedMappingId(response.id);
        usedMappings = response.mappings || autoMappings;
        const listResponse = await JsonServices.listMappings();
        if (listResponse.success) {
          setMappingsList(listResponse.mappings);
        }
        setUnmappedFields(unmapped);
      } else {
        const validationError = validateMappings(tableName, existingMapping.mappings, athenaFields);
        if (validationError) {
          console.error(validationError);
          setInvalidFieldsError(validationError);
          // Reset to correct fields
          const autoMappings = {};
          athenaFields.forEach((field) => {
            if (dependencies[tableName] && field.key in dependencies[tableName]) {
              autoMappings[field.key] = '';
            } else {
              const mappedField = autoMapField(field.key, jsonFields, tableName);
              autoMappings[field.key] = mappedField;
              if (!mappedField && field.required) {
                unmapped.push(field.label);
              }
            }
          });
          const response = await JsonServices.updateMapping(existingMapping.id, { table_name: tableName, mappings: autoMappings });
          usedMappings = response.mappings || autoMappings;
          const listResponse = await JsonServices.listMappings();
          if (listResponse.success) {
            setMappingsList(listResponse.mappings);
          }
          setUnmappedFields(unmapped);
        } else {
          setSelectedMappingId(existingMapping.id);
          usedMappings = existingMapping.mappings;
          athenaFields.forEach((field) => {
            if (!(dependencies[tableName] && field.key in dependencies[tableName]) && !usedMappings[field.key] && field.required) {
              unmapped.push(field.label);
            }
          });
          setUnmappedFields(unmapped);
        }
      }

      const initialMappings = athenaFields.map((field) => {
        const mappingValue = usedMappings[field.key] || '';
        let jsonPath = '';
        let defaultValue = '';
        const isAutoMapped = jsonFields.some(jf => jf.path === mappingValue) && !(dependencies[tableName] && field.key in dependencies[tableName]);
        if (isAutoMapped) {
          jsonPath = mappingValue;
        } else if (mappingValue) {
          defaultValue = mappingValue;
        }
        return {
          athenaField: field.key,
          label: field.label,
          jsonPath,
          defaultValue,
          required: field.required,
          isAutoMapped,
        };
      });
      setTableMappings(initialMappings);
      if (unmapped.length > 0) {
        setErrorMessage(`Required fields not auto-mapped: ${unmapped.join(', ')}. Please set JSON paths or default values.`);
      }
      console.log(`Loaded mappings for ${tableName}:`, initialMappings);
      setCurrentStep(3);
    } catch (error) {
      setErrorMessage("Failed to load mappings. Using auto-generated mappings.");
      const autoMappings = athenaFields.map((field) => {
        const mappedField = dependencies[tableName] && field.key in dependencies[tableName] ? '' : autoMapField(field.key, jsonFields, tableName);
        if (!mappedField && field.required) {
          setUnmappedFields(prev => [...prev, field.label]);
        }
        return {
          athenaField: field.key,
          label: field.label,
          jsonPath: mappedField,
          defaultValue: "",
          required: field.required,
          isAutoMapped: !!mappedField,
        };
      });
      const validationError = validateMappings(tableName, autoMappings.reduce((acc, m) => ({ ...acc, [m.athenaField]: m.jsonPath || m.defaultValue }), {}), athenaFields);
      if (validationError) {
        console.error(validationError);
        setInvalidFieldsError(validationError);
      }
      setTableMappings(autoMappings);
      if (unmappedFields.length > 0) {
        setErrorMessage(`Required fields not auto-mapped: ${unmappedFields.join(', ')}. Please set JSON paths or default values.`);
      }
      setCurrentStep(3);
    } finally {
      setIsLoading(false);
    }
  }, [mappingsList, jsonFields, unmappedFields]);

  // Update field mapping
  const updateFieldMapping = useCallback((idx, field, value) => {
    setTableMappings(prev => {
      const newMappings = [...prev];
      newMappings[idx] = { ...newMappings[idx], [field]: value, isAutoMapped: field === 'jsonPath' && value !== '' };
      return newMappings;
    });
  }, []);

  // Save mappings to backend
  const saveMappingsToBackend = useCallback(async () => {
    if (!selectedTable || tableMappings.length === 0) {
      setErrorMessage("No table selected or no mappings defined.");
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    setInvalidFieldsError(null);

    const payload = {
      table_name: selectedTable,
      mappings: tableMappings.reduce((acc, mapping) => {
        acc[mapping.athenaField] = mapping.jsonPath || mapping.defaultValue || '';
        return acc;
      }, {}),
    };

    const validationError = validateMappings(selectedTable, payload.mappings, TABLE_FIELDS[selectedTable]);
    if (validationError) {
      setInvalidFieldsError(validationError);
      setIsLoading(false);
      return;
    }

    try {
      let response;
      if (selectedMappingId) {
        response = await JsonServices.updateMapping(selectedMappingId, payload);
      } else {
        response = await JsonServices.saveMapping(payload);
        setSelectedMappingId(response.id);
      }

      if (response.success) {
        setSuccessMessage("Mappings saved successfully!");
        const listResponse = await JsonServices.listMappings();
        if (listResponse.success) {
          setMappingsList(listResponse.mappings);
          await startMapping(selectedTable, listResponse.mappings);
        } else {
          setErrorMessage("Mappings saved, but failed to refresh list.");
        }
      } else {
        throw new Error(response.error || "Failed to save mappings.");
      }
    } catch (error) {
      setErrorMessage("Failed to save mappings: " + (error.message || "Unknown error."));
    } finally {
      setIsLoading(false);
    }
  }, [selectedTable, tableMappings, selectedMappingId, startMapping]);

  // Single table import
  const importSingle = useCallback(async () => {
    if (!jsonData) {
      setErrorMessage("No JSON data uploaded.");
      return;
    }
    if (tableMappings.length === 0) {
      setErrorMessage("No field mappings defined.");
      return;
    }
    const missingRequired = tableMappings.filter(m => m.required && !m.jsonPath && !m.defaultValue.trim() && !(dependencies[selectedTable] && m.athenaField in dependencies[selectedTable]));
    if (missingRequired.length > 0) {
      setErrorMessage(`Missing required fields: ${missingRequired.map(m => m.label).join(", ")}`);
      return;
    }
    const tableDeps = dependencies[selectedTable] || {};
    const missingRefs = Object.entries(tableDeps).filter(([field, refName]) => refs[refName].length === 0);
    if (missingRefs.length > 0) {
      setErrorMessage(`Missing references for ${missingRefs.map(([field]) => field).join(", ")}. Import dependent tables first.`);
      return;
    }
    const validationError = validateMappings(selectedTable, tableMappings.reduce((acc, m) => ({ ...acc, [m.athenaField]: m.jsonPath || m.defaultValue }), {}), TABLE_FIELDS[selectedTable]);
    if (validationError) {
      setInvalidFieldsError(validationError);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await saveMappingsToBackend();
      const overrides = Object.entries(tableDeps).reduce((acc, [field, refName]) => {
        acc[field] = (index) => refs[refName][index] || "";
        return acc;
      }, {});
      const csvContent = generateCsv(jsonData, tableMappings, selectedTable, overrides);
      const csvFile = new File([csvContent], `${selectedTable}.csv`, { type: 'text/csv' });
      const formData = new FormData();
      formData.append('excel_file', csvFile);
      formData.append('table_name', selectedTable);

      let importResponse;
      switch (selectedTable) {
        case 'party': importResponse = await JsonServices.partyImport(formData); break;
        case 'partyaddress': importResponse = await JsonServices.partyAddressImport(formData); break;
        case 'partycontact': importResponse = await JsonServices.partyContactImport(formData); break;
        case 'contract': importResponse = await JsonServices.contractImport(formData); break;
        case 'contractpayment': importResponse = await JsonServices.contractPaymentImport(formData); break;
        case 'contractassets': importResponse = await JsonServices.contractAssetsImport(formData); break;
        default: throw new Error(`Import for ${selectedTable} not implemented`);
      }

      if (importResponse.success) {
        const rows = importResponse["value of rows"] || 0;
        const tableLabel = DATABASE_TABLES.find(t => t.name === selectedTable)?.label || selectedTable;
        setSuccessMessage(`Imported ${tableLabel} successfully! Rows: ${rows}`);
        if (refExtractors[selectedTable]) {
          const { arrayKey, refKey, refName } = refExtractors[selectedTable];
          if (importResponse[arrayKey]) {
            const newRefs = importResponse[arrayKey]
              .sort((a, b) => (a.row_index || 0) - (b.row_index || 0))
              .map(item => item[refKey]);
            setRefs(prev => ({ ...prev, [refName]: newRefs }));
          }
        }
        setImportStatus(prev => ({ ...prev, [selectedTable]: 'success' }));
        setCurrentStep(4);
      } else {
        throw new Error(importResponse.error || "Import failed.");
      }
    } catch (error) {
      setErrorMessage("Import failed: " + (error.message || "Unknown error."));
    } finally {
      setIsLoading(false);
    }
  }, [jsonData, tableMappings, selectedTable, refs]);

  // Sequential import
  const importAll = useCallback(async () => {
    setIsImporting(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    setImportErrors({});
    setInvalidFieldsError(null);

    for (let i = importProgress; i < importSequence.length; i++) {
      const table = importSequence[i];
      setImportProgress(i);
      setImportStatus(prev => ({ ...prev, [table]: 'importing' }));

      let mapping = mappingsList.find(m => m.table_name === table);
      if (!mapping) {
        const athenaFields = TABLE_FIELDS[table] || [];
        const autoMappings = athenaFields.map((field) => {
          const mappedField = dependencies[table] && field.key in dependencies[table] ? '' : autoMapField(field.key, jsonFields, table);
          return {
            athenaField: field.key,
            label: field.label,
            jsonPath: mappedField,
            defaultValue: "",
            required: field.required,
            isAutoMapped: !!mappedField,
          };
        });
        const unmapped = autoMappings.filter(m => m.required && !m.jsonPath && !m.defaultValue.trim()).map(m => m.label);
        const validationError = validateMappings(table, autoMappings.reduce((acc, m) => ({ ...acc, [m.athenaField]: m.jsonPath || m.defaultValue }), {}), athenaFields);
        if (validationError) {
          setImportErrors(prev => ({ ...prev, [table]: validationError }));
          setErrorMessage(validationError);
          setImportStatus(prev => ({ ...prev, [table]: 'error' }));
          setSelectedTable(table);
          setTableMappings(autoMappings);
          setIsImporting(false);
          return;
        }
        try {
          const response = await JsonServices.saveMapping({
            table_name: table,
            mappings: autoMappings.reduce((acc, m) => ({ ...acc, [m.athenaField]: m.jsonPath || m.defaultValue }), {}),
          });
          mapping = response;
          const listResponse = await JsonServices.listMappings();
          if (listResponse.success) {
            setMappingsList(listResponse.mappings);
          }
          if (unmapped.length > 0) {
            setUnmappedFields(unmapped);
            setErrorMessage(`Required fields not auto-mapped for ${table}: ${unmapped.join(', ')}.`);
            setImportStatus(prev => ({ ...prev, [table]: 'error' }));
            setSelectedTable(table);
            setTableMappings(autoMappings);
            setIsImporting(false);
            return;
          }
        } catch (error) {
          setImportErrors(prev => ({ ...prev, [table]: `Failed to create mapping: ${error.message}` }));
          setErrorMessage(`Failed to create mapping for ${table}.`);
          setImportStatus(prev => ({ ...prev, [table]: 'error' }));
          setSelectedTable(table);
          setTableMappings(autoMappings);
          setIsImporting(false);
          return;
        }
      }

      const currentMappings = TABLE_FIELDS[table].map((field) => {
        const mappingValue = mapping.mappings[field.key] || '';
        let jsonPath = '';
        let defaultValue = '';
        const isAutoMapped = jsonFields.some(jf => jf.path === mappingValue) && !(dependencies[table] && field.key in dependencies[table]);
        if (isAutoMapped) {
          jsonPath = mappingValue;
        } else if (mappingValue) {
          defaultValue = mappingValue;
        }
        return {
          athenaField: field.key,
          label: field.label,
          jsonPath,
          defaultValue,
          required: field.required,
          isAutoMapped,
        };
      });

      const missingFields = currentMappings.filter(m => m.required && !m.jsonPath && !m.defaultValue.trim() && !(dependencies[table] && m.athenaField in dependencies[table]));
      if (missingFields.length > 0) {
        setImportErrors(prev => ({ ...prev, [table]: `Missing required fields: ${missingFields.map(m => m.label).join(', ')}` }));
        setErrorMessage(`Missing mappings for ${table}: ${missingFields.map(m => m.label).join(', ')}.`);
        setImportStatus(prev => ({ ...prev, [table]: 'error' }));
        setSelectedTable(table);
        setTableMappings(currentMappings);
        setIsImporting(false);
        return;
      }

      const tableDeps = dependencies[table] || {};
      const missingRefs = Object.entries(tableDeps).filter(([field, refName]) => refs[refName].length === 0);
      if (missingRefs.length > 0) {
        setImportErrors(prev => ({ ...prev, [table]: `Missing references: ${missingRefs.map(([field]) => field).join(', ')}` }));
        setErrorMessage(`Missing references for ${table}.`);
        setImportStatus(prev => ({ ...prev, [table]: 'error' }));
        setSelectedTable(table);
        setTableMappings(currentMappings);
        setIsImporting(false);
        return;
      }

      const validationError = validateMappings(table, currentMappings.reduce((acc, m) => ({ ...acc, [m.athenaField]: m.jsonPath || m.defaultValue }), {}), TABLE_FIELDS[table]);
      if (validationError) {
        setImportErrors(prev => ({ ...prev, [table]: validationError }));
        setErrorMessage(validationError);
        setImportStatus(prev => ({ ...prev, [table]: 'error' }));
        setSelectedTable(table);
        setTableMappings(currentMappings);
        setIsImporting(false);
        return;
      }

      try {
        const overrides = Object.entries(tableDeps).reduce((acc, [field, refName]) => {
          acc[field] = (index) => refs[refName][index] || "";
          return acc;
        }, {});
        const csvContent = generateCsv(jsonData, currentMappings, table, overrides);
        console.log(`Generated CSV for ${table}:\n${csvContent}`);
        const csvFile = new File([csvContent], `${table}.csv`, { type: 'text/csv' });
        const formData = new FormData();
        formData.append('excel_file', csvFile);
        formData.append('table_name', table);

        let importResponse;
        switch (table) {
          case 'party': importResponse = await JsonServices.partyImport(formData); break;
          case 'partycontact': importResponse = await JsonServices.partyContactImport(formData); break;
          case 'partyaddress': importResponse = await JsonServices.partyAddressImport(formData); break;
          case 'contract': importResponse = await JsonServices.contractImport(formData); break;
          case 'contractpayment': importResponse = await JsonServices.contractPaymentImport(formData); break;
          case 'contractassets': importResponse = await JsonServices.contractAssetsImport(formData); break;
          default: throw new Error(`Import for ${table} not implemented`);
        }

        if (importResponse.success) {
          setImportStatus(prev => ({ ...prev, [table]: 'success' }));
          if (refExtractors[table]) {
            const { arrayKey, refKey, refName } = refExtractors[table];
            if (importResponse[arrayKey]) {
              const newRefs = importResponse[arrayKey]
                .sort((a, b) => (a.row_index || 0) - (b.row_index || 0))
                .map(item => item[refKey]);
              setRefs(prev => ({ ...prev, [refName]: newRefs }));
            }
          }
          const payload = {
            table_name: table,
            mappings: currentMappings.reduce((acc, m) => ({ ...acc, [m.athenaField]: m.jsonPath || m.defaultValue }), {}),
          };
          if (mapping.id) {
            await JsonServices.updateMapping(mapping.id, payload);
          } else {
            await JsonServices.saveMapping(payload);
          }
          const listResponse = await JsonServices.listMappings();
          if (listResponse.success) {
            setMappingsList(listResponse.mappings);
          }
        } else {
          throw new Error(importResponse.error || "Import failed.");
        }
      } catch (error) {
        setImportErrors(prev => ({ ...prev, [table]: error.message }));
        setErrorMessage(`Error importing ${table}: ${error.message}`);
        setImportStatus(prev => ({ ...prev, [table]: 'error' }));
        setSelectedTable(table);
        setTableMappings(currentMappings);
        setIsImporting(false);
        return;
      }
    }

    setImportProgress(0);
    setImportStatus(importSequence.reduce((acc, table) => ({ ...acc, [table]: 'pending' }), {}));
    setIsImporting(false);
    setCurrentStep(4);
    setSuccessMessage('All contract-related tables imported successfully!');
  }, [importProgress, mappingsList, jsonData, jsonFields, refs]);

  // Reset import
  const resetImport = useCallback(() => {
    setImportProgress(0);
    setImportStatus(importSequence.reduce((acc, table) => ({ ...acc, [table]: 'pending' }), {}));
    setImportErrors({});
    setErrorMessage(null);
    setSuccessMessage(null);
    setRefs({ partyRefs: [], addressRefs: [], contractRefs: [] });
    setUnmappedFields([]);
    setInvalidFieldsError(null);
  }, []);

  // Render steps
  const renderStep1 = () => (
    <div className="text-center">
      <div className="mb-4">
        <label className="btn btn-primary mb-3 d-inline-flex align-items-center gap-2">
          <FiUpload size={20} /> Upload JSON File
          <input type="file" accept=".json" onChange={handleJsonUpload} className="d-none" />
        </label>
      </div>
      {errorMessage && (
        <div className="alert alert-danger d-flex align-items-center gap-2">
          <FiAlertCircle size={18} /> {errorMessage}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div>
      <div className="mb-4">
        <h6 className="text-muted">JSON Preview:</h6>
        <pre className="bg-light p-3 rounded" style={{ maxHeight: 300, overflowY: 'auto', fontSize: '12px' }}>
          {jsonPreview}
        </pre>
      </div>
      <div className="text-right">
        <button className="btn btn-primary btn-sm" onClick={() => setCurrentStep(3)}>
          Start Mapping <FiArrowRight size={18} className="ms-2" />
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => {
    const canImportSingle = tableMappings.every(m => !m.required || m.jsonPath || m.defaultValue.trim() || (dependencies[selectedTable] && m.athenaField in dependencies[selectedTable]));
    return (
      <div>
        <div className="mb-3">
          {/* <label className="form-label">Select Table:</label>
          <select
            className="form-select"
            value={selectedTable}
            onChange={(e) => startMapping(e.target.value)}
            disabled={isLoading || isImporting}
          >
            {DATABASE_TABLES.map((table) => (
              <option key={table.name} value={table.name}>
                {table.label}
              </option>
            ))}
          </select> */}
          {isLoading && (
            <div className="text-muted">
              <small>Loading mappings... <span className="spinner-border spinner-border-sm"></span></small>
            </div>
          )}
        </div>
        {tableMappings.length > 0 && (
          <div className="mb-4">
            <h6 className="text-muted">Field Mappings for {DATABASE_TABLES.find(t => t.name === selectedTable)?.label}:</h6>
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Athena Field</th>
                    <th>JSON Value</th>
                    <th>Default Value</th>
                  </tr>
                </thead>
                <tbody>
                  {tableMappings.map((field) => (
                    <tr key={field.athenaField}>
                      <td>{field.label} {field.required ? '*' : ''}</td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={field.jsonPath}
                          onChange={(e) => updateFieldMapping(tableMappings.indexOf(field), 'jsonPath', e.target.value)}
                          disabled={isLoading || isImporting || (dependencies[selectedTable] && field.athenaField in dependencies[selectedTable])}
                        >
                          <option value="">Select JSON field...</option>
                          {jsonFields.map((jf) => (
                            <option key={jf.path} value={jf.path}>
                              {jf.path} ({jf.type})
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={field.defaultValue}
                          onChange={(e) => updateFieldMapping(tableMappings.indexOf(field), 'defaultValue', e.target.value)}
                          disabled={isLoading || isImporting || (dependencies[selectedTable] && field.athenaField in dependencies[selectedTable])}
                          placeholder="Default value"
                        />
                      </td>
                      
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {unmappedFields.length > 0 && (
          <div className="alert alert-warning d-flex align-items-center gap-2 mt-2">
            <FiAlertCircle size={18} />
            Required fields not auto-mapped: {unmappedFields.join(', ')}. Please set JSON paths or default values.
          </div>
        )}
        {invalidFieldsError && (
          <div className="alert alert-danger d-flex align-items-center gap-2 mt-2">
            <FiAlertCircle size={18} /> {invalidFieldsError}
          </div>
        )}
        {successMessage && (
          <div className="alert alert-success d-flex align-items-center gap-2 mt-2">
            <FiCheck size={18} /> {successMessage}
          </div>
        )}
        {errorMessage && !invalidFieldsError && (
          <div className="alert alert-danger d-flex align-items-center gap-2 mt-2">
            <FiAlertCircle size={18} /> {errorMessage}
          </div>
        )}
        <div className="mb-4">
          <h6 className="text-muted">Contract Import Sequence Status:</h6>
          <ul className="list-group">
            {importSequence.map((table) => (
              <li key={table} className="list-group-item d-flex justify-content-between align-items-center">
                {DATABASE_TABLES.find(t => t.name === table)?.label || table}
                <span className={`badge bg-${importStatus[table] === 'success' ? 'success' : importStatus[table] === 'error' ? 'danger' : importStatus[table] === 'importing' ? 'warning' : 'secondary'}`}>
                  {importStatus[table].charAt(0).toUpperCase() + importStatus[table].slice(1)}
                </span>
              </li>
            ))}
          </ul>
          {Object.keys(importErrors).length > 0 && (
            <div className="mt-2">
              <h6 className="text-danger">Import Errors:</h6>
              <ul>
                {Object.entries(importErrors).map(([table, err]) => (
                  <li key={table}>{table}: {err}</li>
                ))}
              </ul>
            </div>
          )}
          {/* {refs.partyRefs.length > 0 && (
            <div className="mt-2">
              <h6 className="text-muted">Stored Party References:</h6>
              <ul>
                {refs.partyRefs.map((ref, idx) => (
                  <li key={idx}>Row {idx + 1}: {ref}</li>
                ))}
              </ul>
            </div>
          )}
          {refs.addressRefs.length > 0 && (
            <div className="mt-2">
              <h6 className="text-muted">Stored Address References:</h6>
              <ul>
                {refs.addressRefs.map((ref, idx) => (
                  <li key={idx}>Row {idx + 1}: {ref}</li>
                ))}
              </ul>
            </div>
          )}
          {refs.contractRefs.length > 0 && (
            <div className="mt-2">
              <h6 className="text-muted">Stored Contract Numbers:</h6>
              <ul>
                {refs.contractRefs.map((ref, idx) => (
                  <li key={idx}>Row {idx + 1}: {ref}</li>
                ))}
              </ul>
            </div>
          )} */}
        </div>
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setCurrentStep(2)} disabled={isLoading || isImporting}>
            Back
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={saveMappingsToBackend}
            disabled={!selectedTable || tableMappings.length === 0 || isLoading || isImporting}
          >
            <FiSave size={16} className="me-1" /> {isLoading ? 'Saving...' : 'Save Mappings'}
          </button>
          {/* <button
            className="btn btn-primary btn-sm"
            onClick={importSingle}
            disabled={!selectedTable || !canImportSingle || !jsonData || isLoading || isImporting}
          >
            <FiDownload size={16} className="me-1" /> Import Single
          </button> */}
          <button
            className="btn btn-primary btn-sm"
            onClick={importAll}
            disabled={!jsonData || isLoading || isImporting}
          >
            <FiDownload size={16} className="me-1" /> {importProgress > 0 ? 'Resume Import' : 'Import'} {isImporting && <span className="spinner-border spinner-border-sm ms-1"></span>}
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={resetImport}
            disabled={importProgress === 0 || isImporting}
          >
            <FiRefreshCw size={16} className="me-1" /> Reset
          </button>
        </div>
      </div>
    );
  };

  const renderStep4 = () => (
    <div className="text-center">
      <div className="mb-4">
        <FiCheck size={48} className="text-success mb-3" />
        <h5>Data Imported Successfully!</h5>
        <p className="text-muted">Your data has been imported.</p>
      </div>
      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      {errorMessage && (
        <div className="alert alert-danger d-flex align-items-center gap-2">
          <FiAlertCircle size={18} /> {errorMessage}
        </div>
      )}
      <div className="d-flex justify-content-center gap-3">
        <button className="btn btn-primary" onClick={() => setCurrentStep(3)}>
          Edit Mappings
        </button>
        <button className="btn btn-outline-secondary" onClick={() => {
          setCurrentStep(1);
          setJsonData(null);
          setJsonFields([]);
          setTableMappings([]);
          setJsonPreview(null);
          setErrorMessage(null);
          setSuccessMessage(null);
          setSelectedMappingId(null);
          resetImport();
        }}>
          New Import
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-light min-vh-100 py-5">
      <div>
        <div className="row justify-content-center">
          <div className="col-md-10">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom-0 py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">{t("Contract Upload")}</h5>
                  <span className="badge bg-light text-dark">Step {currentStep} of {totalSteps}</span>
                </div>
                <div className="progress mt-3" style={{ height: "4px" }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                    aria-valuenow={currentStep}
                    aria-valuemin="0"
                    aria-valuemax={totalSteps}
                  ></div>
                </div>
              </div>
              <div className="card-body p-4">
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && renderStep2()}
                {currentStep === 3 && renderStep3()}
                {currentStep === 4 && renderStep4()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}